import { Alert, Button, Stack, Typography } from '@mui/material';
import { useFormik } from 'formik';
import { useMemo } from 'react';

import { FProgressBar } from '../../../../../../components/base';
import { FBoxContainer } from '../../../../../../components/baseV2';
import { useFormReadOnly } from '../../../../../common';
import {
  useSaveMedicalField,
  useTreatmentPlanV3Medical,
  useTreatmentPlanV3MedicalPageState,
} from '../hooks';
import { mapMedicalInfoToValues } from '../utils';
import { MEDICAL_FORM_SCHEMA } from '../validationSchema';
import AdditionalDiagnosisList from './AdditionalDiagnosisList';
import AsdDiagnosisFields from './AsdDiagnosisFields';
import BirthInfoFields from './BirthInfoFields';
import ChildDevelopmentFields from './ChildDevelopmentFields';
import MedicalHistoryFields from './MedicalHistoryFields';
import MedicalSelectionSections from './MedicalSelectionSections';
import { TREATMENT_PLAN_V3_SECTION_HEADING_SX } from '../../../../styles';

import type { MedicalPageContentProps } from '../types';

export default function MedicalPageContent({ page, disabled }: MedicalPageContentProps) {
  const readOnly = useFormReadOnly();
  const { isUpdating, isSavingDiagnosis, isSavingSelection } = useTreatmentPlanV3MedicalPageState();
  const {
    updateInfo,
    createDiagnosis,
    updateDiagnosis,
    deleteDiagnosis,
    createSelection,
    deleteSelection,
  } = useTreatmentPlanV3Medical();
  const initialValues = useMemo(
    () => mapMedicalInfoToValues(page.medical_info),
    [page.medical_info],
  );
  const formik = useFormik({
    initialValues,
    validationSchema: MEDICAL_FORM_SCHEMA,
    validateOnMount: true,
    onSubmit: () => undefined,
  });
  const { saveField, hasSaveError, retry } = useSaveMedicalField({
    formik,
    onUpdate: updateInfo,
    disabled: disabled || readOnly,
  });
  const fields = { formik, disabled, readOnly, saveField };

  return (
    <Stack component="form" noValidate onSubmit={formik.handleSubmit} gap={2}>
      {isUpdating ? <FProgressBar /> : null}
      {hasSaveError ? (
        <Alert
          severity="error"
          action={
            <Button onClick={retry} disabled={disabled || readOnly || isUpdating}>
              Retry save
            </Button>
          }
        >
          Some changes did not save. Your edits are still here.
        </Alert>
      ) : null}
      <FBoxContainer>
        <MedicalHistoryFields {...fields} />
      </FBoxContainer>
      <Typography variant="h6" sx={TREATMENT_PLAN_V3_SECTION_HEADING_SX}>
        Diagnosis
      </Typography>
      <FBoxContainer>
        <AsdDiagnosisFields {...fields} />
      </FBoxContainer>
      <Typography variant="h6" sx={TREATMENT_PLAN_V3_SECTION_HEADING_SX}>
        Additional diagnosis
      </Typography>
      <AdditionalDiagnosisList
        entries={page.diagnoses}
        disabled={disabled}
        isSaving={isSavingDiagnosis}
        onCreate={createDiagnosis}
        onUpdate={updateDiagnosis}
        onDelete={deleteDiagnosis}
      />
      <Typography variant="h6" sx={TREATMENT_PLAN_V3_SECTION_HEADING_SX}>
        Birth information
      </Typography>
      <FBoxContainer>
        <BirthInfoFields {...fields} />
      </FBoxContainer>
      <Typography variant="h6" sx={TREATMENT_PLAN_V3_SECTION_HEADING_SX}>
        Early Childhood Development
      </Typography>
      <FBoxContainer>
        <Stack gap={2}>
          <ChildDevelopmentFields {...fields} />
          <MedicalSelectionSections
            selections={page.selections}
            disabled={disabled}
            isSaving={isSavingSelection}
            onAdd={createSelection}
            onDelete={deleteSelection}
          />
        </Stack>
      </FBoxContainer>
    </Stack>
  );
}
